import { ApolloQueryResult } from '@apollo/client'
import { Button, Header, Spacer } from '@spritz-finance/ui'
import React, { useContext, useEffect, useState } from 'react'
import styled from 'styled-components'
import { PageLoading } from '../components/PageLoading'

import {
    AppDomain,
    CurrentUserDataQuery,
    Exact,
    useBillsDataQuery,
    useCurrentUserDataQuery,
    User,
    useUpdateAppDomainMutation,
    useUpdateAttributionSourceMutation,
    useUpdateUserTimezoneMutation,
    useVerificationDataQuery,
} from '../graphql/generated/graphql'
import { useLogout, useUserAuthPayload } from '../modules/app/auth'
import { logger } from '../modules/app/logger'
import { useAttribution } from '../modules/app/attribution'
import { useHighlight } from '../utils/highlight/useHighlight'
import { useApplyReferralCode } from '../modules/signin/SignUp/useStoreReferralCode'

export type Me = CurrentUserDataQuery['me']

type Impersonation = string | null

type IUserContext =
    | {
          loading: boolean
          me: Me
          impersonator: Impersonation
          growsurf: {
              participant: CurrentUserDataQuery['growsurfParticipant']
              secret: CurrentUserDataQuery['growsurfSecret']
              rewardsBalance: CurrentUserDataQuery['rewardsBalance']
          }
          refetch: (
              variables?:
                  | Partial<
                        Exact<{
                            [key: string]: never
                        }>
                    >
                  | undefined
          ) => Promise<ApolloQueryResult<CurrentUserDataQuery>>
      }
    | undefined

const UserState = React.createContext<IUserContext>(undefined)

const ErrorBoundary = styled.div`
    width: 100vw;
    height: 100vh;
    display: flex;
    flex-direction: column;
    justify-content: center;
    align-items: center;
`

export const useCurrentUser = () => {
    const context = useContext(UserState)
    if (!context) throw new Error('Cannot use useCurrentUser outside of a <UserProvider />')
    return context
}

export const useImpersonation = () => {
    const payload = useUserAuthPayload()
    const [impersonator, setImpersonator] = useState<Impersonation>(null)
    useEffect(() => {
        setImpersonator(payload?.impersonator)
    }, [payload])
    return impersonator
}

const userAppDomain = () => {
    switch (window.location.host) {
        case 'quickswap.spritz.finance':
        case 'quickswap-staging.spritz.finance':
            return AppDomain.Quickswap
        default:
            return AppDomain.Spritz
    }
}

export const useCurrentUserQuery = () => {
    const { data, loading, error, startPolling, stopPolling, refetch } = useCurrentUserDataQuery({
        fetchPolicy: 'cache-and-network',
    })
    useApplyReferralCode()
    const [updateTimezone] = useUpdateUserTimezoneMutation()
    const [updateAppDomain] = useUpdateAppDomainMutation()
    const [updateAttributionSource] = useUpdateAttributionSourceMutation()
    const impersonator = useImpersonation()
    const { attributionSource } = useAttribution()
    const { identify } = useHighlight()

    const me = data?.me
    const localTimezone = Intl.DateTimeFormat().resolvedOptions().timeZone

    useEffect(() => {
        if (!me || impersonator) return

        if (me.timezone !== localTimezone) {
            updateTimezone({ variables: { timezone: localTimezone } })
        }
        if (!me.appDomain) {
            updateAppDomain({ variables: { appDomain: userAppDomain() } })
        }
        if (!me.attributionSource) {
            updateAttributionSource({ variables: { attributionSource } })
        }

        identify(me as User)
    }, [
        me,
        localTimezone,
        updateTimezone,
        impersonator,
        updateAppDomain,
        attributionSource,
        identify,
        updateAttributionSource,
    ])

    return {
        loading,
        me,
        growsurf: {
            participant: data?.growsurfParticipant,
            secret: data?.growsurfSecret,
            rewardsBalance: data?.rewardsBalance ?? 0,
        },
        error,
        startPolling,
        stopPolling,
        refetch,
    }
}

export const UserProvider: React.FC = ({ children }) => {
    const { logout } = useLogout()

    const { loading, me, error, growsurf, refetch } = useCurrentUserQuery()
    useVerificationDataQuery({
        fetchPolicy: 'cache-and-network',
    })
    useBillsDataQuery({
        fetchPolicy: 'cache-and-network',
    })
    const impersonator = useImpersonation()

    if (loading) return <PageLoading />

    if (!me || error) {
        logger.error(error)
        return (
            <ErrorBoundary>
                <Header.Med style={{ textAlign: 'center' }}>
                    There was an error with your account. Please try signing back in to your account
                </Header.Med>
                <Spacer height={24} />
                <Button.CTA onClick={logout}>Sign Out</Button.CTA>
            </ErrorBoundary>
        )
    }

    return (
        <UserState.Provider
            value={{
                loading,
                me,
                growsurf,
                refetch,
                impersonator,
            }}
        >
            {children}
        </UserState.Provider>
    )
}
